import useDataGridSearch from '@hooks/useDataGridSearch'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
    Box,
    IconButton,
    Link,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import { DataGridPro } from '@mui/x-data-grid-pro'
import { HabitatAdminUser } from '@types'
import { copyValue } from '@utils'
import { DateTime } from 'luxon'
import { useMemo, useState } from 'react'

interface Props {
    users: HabitatAdminUser[]
}

export default function GroupUserTable({ users }: Props): JSX.Element {
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
        { page: 0, pageSize: 20 }
    )
    const { searchText, requestSearch, filterModel } = useDataGridSearch()

    const columns = useMemo<GridColDef[]>(
        () => [
            {
                field: 'name',
                headerName: 'User Name',
                minWidth: 400,
                display: 'flex',
                valueGetter: (_value, row: HabitatAdminUser) => {
                    return `${row.first_name} ${row.last_name}`
                },
                renderCell: ({ row }) => (
                    <Box>
                        <Typography variant="body1">
                            {row.first_name} {row.last_name}
                        </Typography>
                        <Link
                            href={`/user-deepdive?q=${row.id}`}
                            style={{ fontSize: '0.9em', color: 'darkgray' }}
                        >
                            {row.id}
                        </Link>
                    </Box>
                ),
                renderHeader: () => (
                    <Box>
                        <Typography variant="body1">Name</Typography>
                        <Typography
                            variant="body1"
                            style={{ fontSize: '0.9em', color: 'darkgray' }}
                        >
                            ID
                        </Typography>
                    </Box>
                ),
            },
            {
                field: 'created_date',
                headerName: 'Created Date',
                valueGetter: (value) =>
                    DateTime.fromISO(value).toFormat('DD HH:mm:ss'),
                minWidth: 200,
            },
            {
                field: 'last_login',
                headerName: 'Last Logged In',
                minWidth: 200,
                valueGetter: (value) => {
                    return value
                        ? DateTime.fromISO(value).toFormat('DD HH:mm:ss')
                        : 'Never'
                },
            },
            {
                field: 'deleted_date',
                headerName: 'Deleted Date',
                minWidth: 200,
                valueGetter: (value) => {
                    return value
                        ? DateTime.fromISO(value).toFormat('DD HH:mm:ss')
                        : ''
                },
            },
            {
                field: 'actions',
                headerName: 'Actions',
                minWidth: 150,
                renderCell: ({ row }) => (
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        height={'100%'}
                    >
                        <Tooltip title="Copy User ID">
                            <IconButton
                                onClick={copyValue(row.id)}
                                size="small"
                            >
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Open User page in new tab">
                            <IconButton
                                href={`/user-deepdive?q=${row.id}`}
                                target="_blank"
                                size="small"
                            >
                                <OpenInNewIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ),
            },
        ],
        [users]
    )

    return (
        <Box>
            <TextField
                variant="standard"
                value={searchText}
                onChange={(event) => requestSearch(event.target.value)}
                placeholder="Search..."
            />

            <DataGridPro
                rows={users}
                columns={columns}
                pagination
                paginationMode="client"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 20, 50, { value: -1, label: 'All' }]}
                disableRowSelectionOnClick
                initialState={{
                    filter: {
                        filterModel,
                    },
                }}
                filterModel={filterModel}
            />
        </Box>
    )
}
